import { useAuthQuery } from '@/hooks/useAuthQuery';
import { Icon } from '@iconify/react';
import { Button, Input, Link } from '@nextui-org/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PasswordResetService,
  PasswordResetVerifyResponse,
} from '../../services/password-reset-service';

interface Tenant {
  id: number;
  name: string;
  slug: string;
}

function ResetShell({
  icon,
  spin = false,
  title,
  subtitle,
  children,
}: {
  icon: string;
  spin?: boolean;
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex h-full w-full items-center justify-center px-8 pt-20">
      <div className="flex w-full max-w-sm flex-col gap-4 rounded-large">
        <div className="flex flex-col items-center pb-6">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Icon
              icon={icon}
              className={`text-2xl text-primary ${spin ? 'animate-spin' : ''}`}
            />
          </div>
          <p className="text-xl font-medium">{title}</p>
          {subtitle && (
            <p className="text-small text-default-500 text-center">
              {subtitle}
            </p>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

function resolveResetEmail(
  data: PasswordResetVerifyResponse | { user?: { email?: string } } | undefined
): string {
  const email = data?.user?.email;
  return typeof email === 'string' ? email.trim() : '';
}

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { login } = useAuthQuery();
  const [isVisible, setIsVisible] = useState(false);
  const [isConfirmVisible, setIsConfirmVisible] = useState(false);
  const [formData, setFormData] = useState({
    new_password: '',
    new_password_confirm: '',
  });

  const {
    data: tokenData,
    isLoading: isVerifying,
    error: tokenError,
  } = useQuery({
    queryKey: ['verify-reset-token', token],
    queryFn: () => PasswordResetService.verifyToken(token!),
    enabled: Boolean(token),
    retry: false,
  });

  const verifiedEmail = resolveResetEmail(tokenData);
  const isTokenValid = tokenData?.valid === true && Boolean(verifiedEmail);

  const resetPasswordMutation = useMutation({
    mutationFn: async () => {
      const confirmResult = await PasswordResetService.confirmReset({
        token: token!,
        new_password: formData.new_password,
        new_password_confirm: formData.new_password_confirm,
      });

      const email = resolveResetEmail(confirmResult) || verifiedEmail;
      return login.mutateAsync({
        email,
        password: formData.new_password,
      });
    },
    onSuccess: (data) => {
      const tenants = (data?.tenants ?? []) as Tenant[];

      if (tenants.length === 1) {
        navigate(`/${tenants[0].slug}/home`, { replace: true });
        return;
      }

      navigate('/login', {
        state: { showTenantSelector: tenants.length > 1 },
        replace: true,
      });
    },
    onError: (error: unknown) => {
      console.error('Password reset failed:', error);
      const detail = (
        error as { response?: { data?: { error?: string; detail?: string } } }
      )?.response?.data;

      toast.error(
        detail?.error ??
          detail?.detail ??
          'Failed to reset password. Please try again.'
      );
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const isPasswordValid = formData.new_password.length >= 8;
  const isPasswordMatch =
    formData.new_password === formData.new_password_confirm;
  const isSubmitting = resetPasswordMutation.isPending || login.isPending;
  const isDisabled =
    !isPasswordValid ||
    !isPasswordMatch ||
    !formData.new_password_confirm ||
    isSubmitting;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isDisabled && token && isTokenValid) {
      resetPasswordMutation.mutate();
    }
  };

  if (!token || tokenError || (!isVerifying && !isTokenValid)) {
    return (
      <ResetShell
        icon="solar:clock-circle-linear"
        title="This reset link is not valid"
        subtitle="Reset links expire, and each can only be used once. Request a new one to continue."
      >
        <Button
          color="primary"
          onPress={() => navigate('/forgot-password', { replace: true })}
        >
          Request a new reset link
        </Button>
        <div className="flex items-center justify-center">
          <Link className="text-default-500" href="/login" size="sm">
            Back to Login
          </Link>
        </div>
      </ResetShell>
    );
  }

  if (isVerifying) {
    return (
      <ResetShell
        icon="solar:refresh-linear"
        spin
        title="Checking your reset link..."
      />
    );
  }

  return (
    <ResetShell
      icon="solar:lock-password-linear"
      title="Reset your password"
      subtitle={`Choose a new password for ${verifiedEmail}, then you will be signed in.`}
    >
      <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
        <div className="flex flex-col">
          <Input
            label="New Password"
            name="new_password"
            placeholder="Enter your new password"
            type={isVisible ? 'text' : 'password'}
            variant="bordered"
            value={formData.new_password}
            onChange={handleInputChange}
            isRequired
            classNames={{
              base: '-mb-[2px]',
              inputWrapper:
                'rounded-b-none data-[hover=true]:z-10 group-data-[focus-visible=true]:z-10',
            }}
            endContent={
              <button type="button" onClick={() => setIsVisible(!isVisible)}>
                <Icon
                  className="pointer-events-none text-2xl text-default-400"
                  icon={
                    isVisible ? 'solar:eye-closed-linear' : 'solar:eye-bold'
                  }
                />
              </button>
            }
          />
          <Input
            label="Confirm New Password"
            name="new_password_confirm"
            placeholder="Confirm your new password"
            type={isConfirmVisible ? 'text' : 'password'}
            variant="bordered"
            value={formData.new_password_confirm}
            onChange={handleInputChange}
            isRequired
            classNames={{
              inputWrapper: 'rounded-t-none',
            }}
            endContent={
              <button
                type="button"
                onClick={() => setIsConfirmVisible(!isConfirmVisible)}
              >
                <Icon
                  className="pointer-events-none text-2xl text-default-400"
                  icon={
                    isConfirmVisible
                      ? 'solar:eye-closed-linear'
                      : 'solar:eye-bold'
                  }
                />
              </button>
            }
          />
        </div>

        {formData.new_password && !isPasswordValid && (
          <div className="rounded-lg bg-danger-50 p-3">
            <p className="text-small text-danger-600">
              Password must be at least 8 characters long
            </p>
          </div>
        )}

        {formData.new_password_confirm && !isPasswordMatch && (
          <div className="rounded-lg bg-danger-50 p-3">
            <p className="text-small text-danger-600">
              Passwords do not match
            </p>
          </div>
        )}

        <Button
          color="primary"
          type="submit"
          isLoading={isSubmitting}
          isDisabled={isDisabled}
        >
          {isSubmitting ? 'Signing you in...' : 'Reset password and log in'}
        </Button>
      </form>

      <div className="flex items-center justify-center">
        <Link className="text-default-500" href="/login" size="sm">
          <Icon icon="solar:arrow-left-linear" className="mr-1" />
          Back to Login
        </Link>
      </div>
    </ResetShell>
  );
};

export default ResetPasswordPage;
